import React, { useState } from 'react';
import PageCardItemList from './PageCardItemList';
import PageCardItemModel from '../../Types/PageCardItemModel';
import { get } from '../../Services/http';
import { translate } from '../../Services/translation';
import PageListFilter from '../PageListFilter';
import { ArticleCategoryModel } from '../../Types/ArticleCategoryModel';
import CustomResponsiveSlider from '../CustomResponsiveSlider/CustomResponsiveSlider';
import PageCardItem from '../../Blocks/PageCardItem';

interface Pagination {
  totalCount: number;
  currentPageIndex: number;
  pageSize: number;
  pageCount: number;
}

interface NewsListProps {
  news: PageCardItemModel[];
  pagination: Pagination;
  categories: ArticleCategoryModel[];
  baseUrl: string;
  initialCategories?: ArticleCategoryModel[];
}

const validAspectRatios = [
  'aspect-square',
  'aspect-[3/4]',
  'aspect-[4/3]',
  'aspect-[2/3]',
  'aspect-[3/2]',
];

const getFixedAspectRatios = (index: number) => {
  switch (index) {
    case 0:
    default:
      return 'aspect-[3/4]';
    case 1:
      return 'aspect-square';
    case 2:
      return 'lg:aspect-[2/3] md:aspect-square aspect-[4/3]';
    case 3:
      return 'xl:aspect-[4/3] lg:aspect-square aspect-[2/3]';
    case 4:
      return 'xl:aspect-square aspect-[2/3]';
    case 5:
      return 'xl:aspect-[2/3] lg:aspect-[3/4] aspect-[4/3]';
    case 6:
      return 'xl:aspect-[3/4] lg:aspect-[2/3] aspect-[4/3]';
    case 7:
      return 'xl:aspect-square lg:aspect-[4/3] aspect-square';
  }
};

const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
};

const generateAspectRatio = (
  invalidAspectRatio: string | undefined = undefined
) => {
  let aspectRatio =
    validAspectRatios[getRandomInt(0, validAspectRatios.length - 1)] ??
    'aspect-square';

  // Generate new aspect ratio until we get a valid aspect ratio
  if (invalidAspectRatio) {
    while (aspectRatio == invalidAspectRatio) {
      let newAspectRatio =
        validAspectRatios[getRandomInt(0, validAspectRatios.length - 1)];
      if (!newAspectRatio) {
        return 'aspect-square';
      }
      aspectRatio = newAspectRatio;
    }
  }

  return aspectRatio;
};

const withInitialProps = (items: PageCardItemModel[]) => {
  var _items = [...items];
  items.forEach((n, idx) => {
    if (!n.aspectRatio) {
      n.aspectRatio =
        idx < 8 ? getFixedAspectRatios(idx) : generateAspectRatio();
    }
  });
  return _items;
};

const NewsList = ({
  news: initialNews,
  pagination: initialPagination,
  categories,
  baseUrl,
  initialCategories,
}: NewsListProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState(withInitialProps(initialNews));
  const [pagination, setPagination] = useState(initialPagination);
  const [currentPage, setCurrentPage] = useState(
    pagination.currentPageIndex
  );
  const [activeTagIds, setActiveTagIds] = useState<string[]>(
    initialCategories?.map((c) => c.tagId) ?? []
  );

  const fetchArticles = (
    activeFilters?: string[],
    isLoadMore?: boolean
  ) => {
    const searchParams = new URLSearchParams();

    if (activeFilters && activeFilters.length > 0) {
      searchParams.append('tags', activeFilters.join(','));
    }

    if (isLoadMore === true) {
      searchParams.append('pageNumber', `${currentPage + 1}`);
    }

    const url = `/api/news?${searchParams.toString()}`;
    return get(url).then((res) => res.json());
  };

  const onFilterChange = (activeFilters?: string[]) => {
    setIsLoading(true);
    setCurrentPage(1);
    fetchArticles(activeFilters, false)
      .then((data: NewsListProps) => {
        setPagination(data.pagination);
        setItems(withInitialProps(data.news));
      })
      .finally(() => {
        setIsLoading(false);
      });

    updateUrl(activeFilters);
  };

  const updateUrl = (activeFilters?: string[]) => {
    if (!activeFilters || activeFilters.length == 0) {
      window.history.pushState(null, '', baseUrl);
      return;
    }

    if (activeFilters.length > 1) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('tags', activeFilters.join(','));
      window.history.pushState(
        null,
        '',
        `${baseUrl}?${searchParams.toString()}`
      );
      return;
    }

    const category = categories.find((c) => c.tagId == activeFilters[0]);
    if (!category) return;
    window.history.pushState(null, '', category.url);
  };

  const getSkeletonItems = (count: number): PageCardItemModel[] => {
    return Array.from({ length: count }).map((_) => ({
      aspectRatio: generateAspectRatio(),
      skeleton: true,
      title: '',
      tags: [],
    }));
  };

  const onLoadMoreClick = () => {
    setIsLoading(true);
    let itemsWithSkeletons = [
      ...items,
      ...getSkeletonItems(
        Math.min(pagination.pageSize, pagination.totalCount - items.length)
      ),
    ];
    setItems(itemsWithSkeletons);

    fetchArticles(activeTagIds, true)
      .then((data: NewsListProps) => {
        setCurrentPage(currentPage + 1);
        // Set correct aspect ratio, from the skeleton cards
        const skeletonItems = itemsWithSkeletons.filter((i) => i.skeleton);

        data.news.forEach((item, idx) => {
          item.aspectRatio =
            skeletonItems[idx]?.aspectRatio ?? generateAspectRatio();
        });

        // Remove placeholders for this group key
        setTimeout(() => {
          setItems([...items, ...data.news]);
        }, 150);
      })
      .catch((err) => {
        // Unset skeleton cards
        setItems(items);
        console.log(err);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="mb-4">
      <PageListFilter
        categories={categories}
        onChange={onFilterChange}
        activeTagIds={activeTagIds}
        setActiveTagIds={setActiveTagIds}
      />
      <div className="hidden md:block">
        <PageCardItemList items={items} isLoading={isLoading} />
      </div>
      <div className="container md:hidden">
        <CustomResponsiveSlider
          numPerRow={2}
          items={items}
          showSlider={false}
          mobileLayout="large"
          itemRenderer={(item, key, className) => (
            <PageCardItem
              item={item}
              aspectRatio="aspect-[3/4]"
              className={className}
              key={key}
            />
          )}
        />
      </div>
      {pagination.totalCount > items.filter((i) => !i.skeleton).length && (
        <div className="my-6 flex justify-center lg:mt-10">
          <button
            className="btn btn--outline btn--large"
            onClick={onLoadMoreClick}
          >
            {translate('general.load-more')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsList;
